import React, { useState, useEffect, useRef, useCallback } from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import { useDidUpdate } from 'rooks';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { PayloadAction } from '@reduxjs/toolkit';
import queryString from 'query-string';
import pluralize from 'pluralize';
import { Spin, Row, Col, Button } from 'antd';

import {
  CustomerStatusFilterEnum,
  CustomerInfoResponseModel,
  CustomerPagedResponseModel,
} from 'api/backofficeApiClient';
import ClientCard from '../ClientCard';
import { ClientFilterTypes } from '../enums';
import {
  getClientRequests,
  getCustomerListExcel,
} from 'redux/reducers/clientSlice';
import ClientsFilter from '../ClientsFilter';
import { isAdminSelector } from 'redux/selectors/authenticationSelectors';
import { typeConversionToStringArray } from 'utils/convertUtils';
import BaseSearch from 'components/wrappers/BaseSearch';
import { useAppSelector } from 'hooks/useAppSelector';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { CustomerCompaniesFilterEnum } from 'api/adminApiClient';

import styles from '../ClientRequestsSection/ClientRequestsSection.module.less';
import { downloadFileByObjectURL } from 'utils/fileUtils';
import BaseAlert from 'components/wrappers/BaseAlert';
import { ApiErrorMessage } from 'constants/ErrorMessages';

type Props = {
  isAdmin?: boolean;
};

const noClientsMessage =
  'You have no approved customers. Perhaps it is time to create one? Let us know if we can help.';

export interface ClientsFilters {
  [ClientFilterTypes.Company]?: string[];
}

export interface ClientQueryType {
  query: string;
  section: string;
  statusFilter?: CustomerStatusFilterEnum;
  [ClientFilterTypes.Company]?: string[];
}

export default function ApprovedClientsSection(props: Props) {
  const latestRequestId = useRef(0);
  const { isAdmin } = props;
  const infiniteScrollParentRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { query, company, section } = queryString.parse(location.search);
  const initialFilters: ClientsFilters = {
    [ClientFilterTypes.Company]: typeConversionToStringArray(company),
  };
  const [filters, setFilters] = useState<ClientsFilters>(initialFilters);
  const [clients, setClients] = useState<CustomerInfoResponseModel[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const isAdminRole = useAppSelector(isAdminSelector);
  const [hasMore, setHasMore] = useState(true);
  const [isInitialDataLoaded, setIsInitialDataLoaded] = useState(false);
  const dispatch = useAppDispatch();

  useDidUpdate(() => {
    const queryData: ClientQueryType = {
      query,
      section,
      [ClientFilterTypes.Company]: filters[ClientFilterTypes.Company],
    };
    navigate(`${location.pathname}?${queryString.stringify(queryData)}`, {
      replace: true,
    });
  }, [filters, query]);

  useEffect(() => {
    initInfinityScroll();
  }, [filters, query]);

  const initInfinityScroll = () => {
    setIsVisible(false);
    setClients([]);
    setIsInitialDataLoaded(false);
    setHasMore(true);
    setTotalCount(0);
    const timer = setTimeout(() => {
      // to destroy InfiniteScroll
      setIsVisible(true);
    }, 0);

    return () => clearTimeout(timer);
  };

  const onSetClients = useCallback(
    (result: PayloadAction<CustomerPagedResponseModel>) => {
      const loadedClients = result.payload.details || [];
      const totalCount = result.payload.totalCount || 0;
      const newClients = [...clients, ...loadedClients];
      const hasMoreRows = newClients.length < totalCount;

      setHasMore(hasMoreRows);
      setClients(newClients);
      setTotalCount(totalCount);
    },
    [clients],
  );

  const loadClients = useCallback(
    async (pageNumber: number) => {
      const requestId = Date.now();
      latestRequestId.current = requestId;

      const result = await dispatch(
        getClientRequests({
          pageNumber,
          isAdminRole,
          requestId,
          searchQuery: query ? query.trim() : undefined,
          statusFilter: CustomerStatusFilterEnum.NewCustomerSetupComplete,
          companyIds: filters[ClientFilterTypes.Company],
        }),
      );

      if (result.meta.arg.requestId === latestRequestId.current) {
        if (getClientRequests.fulfilled.match(result)) {
          onSetClients(result);
        } else {
          setHasMore(false);
        }

        setIsInitialDataLoaded(true);
      }
    },
    [dispatch, onSetClients, isAdminRole, filters, query],
  );

  const pluralizedClients = pluralize('customer', totalCount, true);

  const handleDownloadCustomerList = async () => {
    const result = await dispatch(getCustomerListExcel());

    if (getCustomerListExcel.fulfilled.match(result)) {
      downloadFileByObjectURL(result.payload.url, result.payload.fileName);
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  return (
    <div ref={infiniteScrollParentRef} className="page-wrapper scroll-box">
      <div className="page-box page-holder page-inner">
        <div className="page-inner-container">
          <Row gutter={[20, 10]}>
            <Col xs={24} md={6}>
              <BaseSearch
                searchQuery={query}
                placeholder="Search by customer name, ID, invoice name (Legal/DBA)"
              />
            </Col>
            {isAdmin && (
              <Col xs={24} md={6}>
                <ClientsFilter<ClientsFilters>
                  filters={filters}
                  onChange={setFilters}
                  value={filters[ClientFilterTypes.Company]}
                  customerCompaniesFilter={CustomerCompaniesFilterEnum.Customer}
                />
              </Col>
            )}
            {isAdmin && (
              <div className="d-flex" style={{ marginLeft: 'auto' }}>
                <Col>
                  <Button className={styles.btn}>
                    <Link to="/customer-bulk-import">Import customers</Link>
                  </Button>
                </Col>
                <Col>
                  <Button
                    className="with-icon"
                    onClick={handleDownloadCustomerList}
                    icon={<span className="icon icon-xls-file" />}
                  >
                    Export Customer List
                  </Button>
                </Col>
              </div>
            )}

            <Col xs={24}>
              {isInitialDataLoaded && (
                <>
                  {!!totalCount && (
                    <p className="page-total-count">{pluralizedClients}</p>
                  )}

                  {!totalCount && (
                    <span className="no-data-msg">
                      {isAdmin
                        ? 'There are no approved customers.'
                        : noClientsMessage}
                    </span>
                  )}
                </>
              )}
            </Col>
          </Row>
          {isVisible && (
            <InfiniteScroll
              pageStart={-1}
              loadMore={loadClients}
              hasMore={hasMore}
              loader={<Spin key={0} />}
              useWindow={false}
              getScrollParent={() => infiniteScrollParentRef.current}
            >
              <Row gutter={[20, 20]}>
                {clients.map((client, index) => (
                  <Col
                    key={`${client.customerId}-${index}`}
                    xs={24}
                    md={12}
                    className="d-flex"
                  >
                    <ClientCard
                      client={client}
                      enableStatus={false}
                      className="stretch-card"
                    />
                  </Col>
                ))}
              </Row>
            </InfiniteScroll>
          )}
        </div>
      </div>
    </div>
  );
}
